// Web scraper provider types (web_scraper_provider_design.md §7, §8.5).
// Mirrors the backend CrawlConfig / ProviderCrawlRun / ProviderCrawlItem
// shapes the admin API returns; keep in sync with
// packages/backend/src/kernel/services/providers/webScraper/types.ts.

export type BoundaryMode = "host" | "host_and_subdomains" | "prefixes";
export type ReviewPolicy = "always" | "auto";
export type CrawlSchedule = "off" | "daily" | "weekly" | "monthly";

export interface CrawlConfig {
    seedUrls: string[];
    boundary: { mode: BoundaryMode; prefixes?: string[] };
    useSitemap: boolean;
    followLinks: boolean;
    excludePatterns: string[];
    includePattern?: string | null;
    maxDepth: number;
    maxPages: number;
    maxDocuments: number;
    maxBytes: number;
    maxPageBytes: number;
    maxDocumentBytes: number;
    maxMediaBytes: number;
    maxRunMs: number;
    includeDocuments: boolean;
    documentsSameHostOnly: boolean;
    includeMedia: boolean;
    minBodyWords: number;
    minDocumentLinks: number;
    contentSelector?: string;
    removeSelectors?: string[];
    /** Crawl result email recipients (design §8.6); empty = no email. */
    notifyEmails?: string[];
}

/** Provider defaults (design §12). Duplicates backend defaultCrawlConfig(). */
export const CRAWL_CONFIG_DEFAULTS = {
    maxDepth: 5,
    maxPages: 500,
    maxDocuments: 200,
    /** maxRunMs in minutes; the form edits minutes. */
    maxRunMinutes: 30,
    includeDocuments: true,
    includeMedia: false,
    useSitemap: true,
    followLinks: true
} as const;

export type CrawlRunStatus =
    | "running"
    | "awaiting_review"
    | "applying"
    | "complete"
    | "incomplete"
    | "discarded"
    | "expired"
    | "error";

export type CrawlOutcome =
    | "new"
    | "modified"
    | "renamed"
    | "unchanged"
    | "removed"
    | "skipped"
    | "failed";

export type CrawlItemKind = "page" | "document" | "media" | "other";

export interface CrawlRunCounts {
    discovered?: number;
    pages?: number;
    documents?: number;
    media?: number;
    new?: number;
    modified?: number;
    renamed?: number;
    unchanged?: number;
    removed?: number;
    excluded?: number;
    skipped?: number;
    failed?: number;
    skippedByReason?: Record<string, number>;
    failedByReason?: Record<string, number>;
}

export interface CrawlRun {
    id: string;
    contentItemProviderId: number;
    trigger: "manual" | "scheduled" | "page";
    status: CrawlRunStatus;
    crawlComplete: boolean;
    reviewReasons?: string[] | null;
    /** Why the crawl did not finish (crawler reasons; empty or absent when it did). */
    incompleteReasons?: string[] | null;
    startedAt: string;
    crawledAt?: string | null;
    expiresAt?: string | null;
    appliedAt?: string | null;
    finishedAt?: string | null;
    counts: CrawlRunCounts;
    bytesFetched: number | null;
    samples?: Record<string, string[]> | null;
    error?: string | null;
}

export interface CrawlItem {
    id: string;
    crawlRunId: string;
    canonicalUrl: string;
    fetchedUrl?: string | null;
    referrerUrl?: string | null;
    depth: number;
    kind: CrawlItemKind;
    outcome: CrawlOutcome;
    reason?: string | null;
    httpStatus?: number | null;
    title?: string | null;
    name?: string | null;
    providerPath?: string | null;
    sourceType?: string | null;
    mimeType?: string | null;
    sizeBytes?: number | null;
    wordCount?: number | null;
    fileUid?: string | null;
    decision: "include" | "exclude";
    decisionSource: "auto" | "admin" | "rule";
    excerpt?: string | null;
    outline?: { level: number; text: string }[] | null;
    note?: string | null;
    error?: string | null;
}

export interface CrawlItemsPage {
    total: number;
    page: number;
    pageSize: number;
    /** Every reason present in the run (not just this page), for the Reason filter. */
    reasons?: { reason: string; count: number }[];
    items: CrawlItem[];
}

export interface CrawlItemPreview {
    kind: CrawlItemKind;
    url: string;
    title?: string | null;
    html?: string | null;
    text?: string | null;
    outline?: { level: number; text: string }[] | null;
    wordCount?: number;
    raw?: string | null;
    note?: string;
}

export type CrawlItemSort = "title" | "canonicalUrl" | "sizeBytes";

/** Inventory query; outcome, decision, kind and reason accept a comma-separated list (any of). */
export interface CrawlItemsQuery {
    outcome?: CrawlOutcome | string;
    decision?: "include" | "exclude" | string;
    kind?: CrawlItemKind | string;
    reason?: string;
    q?: string;
    sortBy?: CrawlItemSort;
    sortOrder?: "asc" | "desc";
    page?: number;
    pageSize?: number;
}
